import { useState, useEffect, useMemo } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import deAT from "date-fns/locale/de";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { bookingsApi, matchesApi, seriesApi, usersApi } from "./api";
import { Box, Typography, Modal, Button, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Stack, TextField, Alert } from "@mui/material";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import SportsScoreRoundedIcon from "@mui/icons-material/SportsScoreRounded";
import { playerName } from "./rankingUtils";

const locales = { de: deAT };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

export default function BookingCalendar({ user }) {
  const [bookings, setBookings] = useState([]);
  const [series, setSeries] = useState([]);
  const [users, setUsers] = useState([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('day');
  const [trigger, setTrigger] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
//  const [modalSlot, setModalSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [opponentId, setOpponentId] = useState("");
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultSets, setResultSets] = useState([{ home: "", away: "" }, { home: "", away: "" }, { home: "", away: "" }]);
  const [resultError, setResultError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dateJumpOpen, setDateJumpOpen] = useState(false);
  const [jumpDate, setJumpDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Platzwahl
  const [platzNummer, setPlatzNummer] = useState(1);

  useEffect(() => {
    const fetchAll = async () => {
      const [loadedBookings, loadedSeries, loadedUsers] = await Promise.all([
        bookingsApi.list(), seriesApi.list(), usersApi.list()
      ]);
      setBookings(loadedBookings.bookings || loadedBookings);
      setSeries(loadedSeries.series || loadedSeries);
      setUsers(loadedUsers.users || loadedUsers);
    };
    fetchAll();
  }, [trigger]);

  // Map: userId => name/email
  const userMap = useMemo(() => {
    const m = {};
    users.forEach(u => {
      m[u.id] = u.firstname || u.lastname
        ? `${u.firstname || ""} ${u.lastname || ""}`.trim()
        : (u.email || u.id);
    });
    return m;
  }, [users]);

  const approvedOpponents = useMemo(() => users
    .filter(u => u.id !== user.uid && u.approved && u.active !== false)
    .sort((a, b) => playerName(a).localeCompare(playerName(b))), [users, user.uid]);

  // Zusammengehörige, direkt aufeinanderfolgende Stunden werden als ein Termin dargestellt.
  const bookingEvents = useMemo(() => {
    const placeBookings = bookings
      .filter(b => Number(b.platzNummer) === Number(platzNummer))
      .sort((a, b) => a.date.localeCompare(b.date) || Number(a.hour) - Number(b.hour));
    const groups = [];
    placeBookings.forEach(booking => {
      const key = booking.bookingGroupId || booking.id;
      const previous = groups[groups.length - 1];
      const mayMerge = previous && previous.key === key && previous.date === booking.date && previous.lastHour + 1 === Number(booking.hour);
      if (mayMerge) {
        previous.items.push(booking);
        previous.lastHour = Number(booking.hour);
      } else {
        groups.push({ key, date: booking.date, firstHour: Number(booking.hour), lastHour: Number(booking.hour), items: [booking] });
      }
    });
    return groups.map(group => {
      const primary = group.items.find(item => item.resultPrimary !== false) || group.items[0];
      const bookerFullName = userMap[primary.userId] || primary.userId;
      const opponentFullName = primary.opponentId ? (userMap[primary.opponentId] || primary.opponentId) : "";
      const firstName = fullName => String(fullName).trim().split(/\s+/)[0];
      const fullTitle = `${bookerFullName}${opponentFullName ? ` gegen ${opponentFullName}` : ""}`;
      const shortTitle = `${firstName(bookerFullName)}${opponentFullName ? ` gegen ${firstName(opponentFullName)}` : ""}`;
      const start = new Date(`${group.date}T${String(group.firstHour).padStart(2, "0")}:00`);
      const end = new Date(`${group.date}T${String(group.lastHour + 1).padStart(2, "0")}:00`);
      return {
        id: primary.id,
        bookingIds: group.items.map(item => item.id),
        title: shortTitle,
        fullTitle,
        start, end, resource: primary,
        platzNummer: primary.platzNummer,
        isOwn: primary.userId === user.uid,
        mayEditResult: [primary.userId, primary.opponentId].includes(user.uid),
        bookerName: bookerFullName,
        opponentName: opponentFullName
      };
    });
  }, [bookings, user.uid, userMap, platzNummer]);

  // Serientermine-Events für aktuellen Platz, pro Block nur einmal
  const seriesEvents = useMemo(() => {
    let events = [];
    const minDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const maxDate = new Date(date.getFullYear(), date.getMonth() + 2, 0);
    series.forEach(s => {
      if (Number(s.place) !== Number(platzNummer)) return;
      let fromD = new Date(s.fromDate);
      let toD = new Date(s.toDate);
      for (let day = new Date(fromD); day <= toD; day.setDate(day.getDate() + 1)) {
        if (day < minDate || day > maxDate) continue;
        if (day.getDay() !== s.weekday) continue;
        s.hours.forEach(hour => {
          const evStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0);
          const evEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour + 1, 0, 0);
          const teilnehmer = (s.users || []).map(uid =>
            userMap[uid] || uid
          ).filter(Boolean).join(", ");
          events.push({
            id: `serie-${s.id}-${evStart.toISOString()}-p${s.place}`,
            title: s.name || "Serientermin",
            start: evStart,
            end: evEnd,
            resource: { seriesId: s.id, teilnehmer: teilnehmer, ...s },
            platzNummer: s.place,
            isSeries: true,
            tooltipTeilnehmer: teilnehmer
          });
        });
      }
    });
    return events;
  }, [series, date, userMap, platzNummer]);

  // Alle Events für Platz
  const events = useMemo(() => [...seriesEvents, ...bookingEvents], [seriesEvents, bookingEvents]);

  // Zukünftige eigene Einzelbuchungen, egal Platz
  const futureBookings = useMemo(() => bookings.filter(b =>
    b.userId === user.uid &&
    (
      b.date > format(new Date(), "yyyy-MM-dd") ||
      (b.date === format(new Date(), "yyyy-MM-dd") && b.hour > new Date().getHours())
    )
  ), [bookings, user.uid]);

  // Ist User Mitglied in einer SERIE, heute im Zeitraum?
  function userHasAnyActiveFutureSeries() {
    const now = new Date();
    return series.some(s => {
      if (!s.users.includes(user.uid)) return false;
      const from = new Date(s.fromDate);
      const to = new Date(s.toDate);
      return now >= from && now <= to;
    });
  }
  const userHasActiveFutureSeries = userHasAnyActiveFutureSeries();

  // Ist User im Slot in Serie (egal Platz/Serie)?
  function userHasSeriesAtSlot(slotDateObj, slotHour) {
    return series.some(s => {
      if (!s.users.includes(user.uid)) return false;
      let thisDate = format(slotDateObj, "yyyy-MM-dd");
      let fromD = s.fromDate, toD = s.toDate;
      let isInRange = (thisDate >= fromD && thisDate <= toD);
      let isRightWeekday = slotDateObj.getDay() === s.weekday;
      let isRightHour = s.hours.includes(slotHour);
      return isInRange && isRightWeekday && isRightHour;
    });
  }
  // Blockiere Doppelbuchung im selben Slot (auch wenn User in Serie dort!)
//  function userHasConflictAtSlot(slotDateObj, slotHour) {
//    const alreadyBooked = bookings.some(
//      b => b.userId === user.uid && b.date === format(slotDateObj, "yyyy-MM-dd") && b.hour === slotHour
//    );
//    const inSeries = userHasSeriesAtSlot(slotDateObj, slotHour);
//    return alreadyBooked || inSeries;
//  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={platzNummer}
          exclusive
          onChange={(_event, value) => { if (value) setPlatzNummer(value); }}
          aria-label="Platzwahl"
        >
          <ToggleButton value={1}>Platz 1</ToggleButton>
          <ToggleButton value={2}>Platz 2</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="h6">{`Platzbuchungen – Platz ${platzNummer}`}</Typography>
      <Calendar
        date={date}
        onNavigate={setDate}
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        components={{
          week: {
            header: ({ date: headerDate }) => (
              <Box className="calendar-day-header">
                <Box component="span" className="calendar-weekday">{["So", "Mo", "Die", "Mi", "Do", "Fr", "Sa"][headerDate.getDay()]}</Box>
                <Box component="span" className="calendar-day-date">{format(headerDate, "dd.MM.")}</Box>
              </Box>
            ),
            event: ({ event }) => <Box className="calendar-booking-title">{event.title}</Box>,
          },
          day: {
            header: ({ date: headerDate }) => (
              <Box className="calendar-day-header"><Box component="span" className="calendar-weekday">{["So", "Mo", "Die", "Mi", "Do", "Fr", "Sa"][headerDate.getDay()]}</Box><Box component="span" className="calendar-day-date">{format(headerDate, "dd.MM.")}</Box></Box>
            ),
            event: ({ event }) => <Box className="calendar-booking-title">{event.title}</Box>,
          },
          toolbar: toolbarProps => (
            <Box className="rbc-toolbar" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <span className="rbc-btn-group">
                <button type="button" onClick={() => toolbarProps.onNavigate("TODAY")}>Heute</button>
                <button type="button" onClick={() => toolbarProps.onNavigate("PREV")}>Zurück</button>
                <button type="button" onClick={() => toolbarProps.onNavigate("NEXT")}>Weiter</button>
              </span>
              <Button
                className="rbc-toolbar-label calendar-jump-label"
                variant="text"
                title="Zu einem bestimmten Datum springen"
                onClick={() => {
                  setJumpDate(format(date, "yyyy-MM-dd"));
                  setDateJumpOpen(true);
                }}
              >
                {toolbarProps.label}
              </Button>
              <span className="rbc-btn-group">
                <button type="button" className={view === "month" ? "rbc-active" : ""} onClick={() => toolbarProps.onView("month")}>Monat</button>
                <button type="button" className={view === "week" ? "rbc-active" : ""} onClick={() => toolbarProps.onView("week")}>Woche</button>
                <button type="button" className={view === "day" ? "rbc-active" : ""} onClick={() => toolbarProps.onView("day")}>Tag</button>
              </span>
            </Box>
          )
        }}
        views={["month", "week", "day"]}
        view={view}
        onView={setView}
        style={{ height: 600, background: "white" }}
        defaultView="day"
        popup
        step={60}
        timeslots={1}
        selectable="ignoreEvents"
        min={new Date(1970, 1, 1, 8, 0)}
        max={new Date(1970, 1, 1, 22, 0)}
        eventPropGetter={event => ({
          style: {
            background: event.isSeries ? "#ffb300"
              : event.isOwn ? "#81c784"
                : "#1976d2",
            color: "#fff",
            borderRadius: 4,
            opacity: 0.85
          }
        })}
        culture="de"
        messages={{
          today: "Heute", previous: "Zurück", next: "Weiter",
          month: "Monat", week: "Woche", day: "Tag", agenda: "Terminübersicht",
          date: "Datum", time: "Zeit", event: "Termin", noEventsInRange: "In diesem Zeitraum gibt es keine Termine.",
          showMore: total => `+ ${total} weitere`
        }}
        formats={{
          timeGutterFormat: "HH:mm",
          dayRangeHeaderFormat: ({ start, end }) => `${format(start, "dd.MM.yyyy")} – ${format(end, "dd.MM.yyyy")}`,
          dayHeaderFormat: dateValue => format(dateValue, "EEEE, dd.MM.yyyy", { locale: deAT }),
          monthHeaderFormat: dateValue => format(dateValue, "MMMM yyyy", { locale: deAT }),
          eventTimeRangeFormat: () => "",
        }}
        tooltipAccessor={event => event.isSeries
          ? `Serientermin: ${event.title}\nTeilnehmer: ${event.tooltipTeilnehmer}\n${event.start.toLocaleDateString()} ${event.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}-${event.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`
          : `${event.fullTitle || event.title}\n${event.start.toLocaleDateString()} ${event.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} bis ${event.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`
        }
        onSelectSlot={async slotInfo => {
          if (!user.isAdmin && user.active === false) {
            alert("Dein Zugang ist derzeit inaktiv. Buchungen sind erst nach Einzahlung des Mitgliedsbeitrags wieder möglich.");
            return;
          }
          const slotStart = slotInfo.start;
          const slotEnd = slotInfo.end;
          // Alle gewünschten Stunden
          let slotHours = [];
          for (let hour = slotStart.getHours(); hour < slotEnd.getHours(); hour++) {
            slotHours.push(hour);
          }
          // 1. Aktive Serie-Logik (Limit 1)
          if (userHasActiveFutureSeries && (futureBookings.length + slotHours.length) > 1) {
            alert("Du bist aktuell Mitglied einer Serie ('Rad') und darfst insgesamt nur eine zukünftige Buchung haben.");
            return;
          }
          // 2. Normale 2er-Logik
          if (!userHasActiveFutureSeries && (futureBookings.length + slotHours.length) > 2) {
            alert("Du hast bereits zwei zukünftige Buchungen oder würdest das Limit überschreiten.");
            return;
          }
          // Gibt es eine der Stunden, die schon belegt/Serie ist?
          let alreadyBookedOrInSeries = slotHours.filter(hour =>
            bookings.some(b => b.date === format(slotStart, "yyyy-MM-dd") && b.hour === hour && Number(b.platzNummer) === Number(platzNummer))
            || userHasSeriesAtSlot(slotStart, hour)
          );
          if (alreadyBookedOrInSeries.length > 0) {
            alert(`Die folgenden Stunden sind schon belegt oder Serie: ${alreadyBookedOrInSeries.join(", ")} Uhr.`);
            slotHours = slotHours.filter(hour => !alreadyBookedOrInSeries.includes(hour));
            if (slotHours.length === 0) return;
          }
          // Der optionale Gegner wird nur angezeigt und zählt nicht gegen dessen Buchungslimit.
          setPendingBooking({ slotStart, slotHours });
          setOpponentId("");
          setBookingDialogOpen(true);
        }}
        onSelectEvent={event => {
          setSelectedEvent(event);
          setModalOpen(true);
        }}
        longPressThreshold={600}
      />
      <Dialog open={dateJumpOpen} onClose={() => setDateJumpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Zu einem Datum springen</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Wähle das gewünschte Datum. Die aktuelle {view === "day" ? "Tagesansicht" : view === "month" ? "Monatsansicht" : "Wochenansicht"} bleibt erhalten.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="date"
            label="Datum"
            value={jumpDate}
            onChange={event => setJumpDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDateJumpOpen(false)}>Abbrechen</Button>
          <Button variant="contained" disabled={!jumpDate} onClick={() => {
            const [year, month, day] = jumpDate.split("-").map(Number);
            setDate(new Date(year, month - 1, day));
            setDateJumpOpen(false);
          }}>Zum Datum</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bookingDialogOpen} onClose={() => setBookingDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Platz reservieren</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{pendingBooking && `${format(pendingBooking.slotStart, "dd.MM.yyyy")} · ${pendingBooking.slotHours.join(", ")} Uhr · Platz ${platzNummer}`}</Typography>
          <FormControl fullWidth><InputLabel id="opponent-label">Gegner/in (optional)</InputLabel>
            <Select labelId="opponent-label" value={opponentId} label="Gegner/in (optional)" onChange={e => setOpponentId(e.target.value)}>
              <MenuItem value=""><em>Kein Gegner ausgewählt</em></MenuItem>
              {approvedOpponents.map(opponent => <MenuItem key={opponent.id} value={opponent.id}>{playerName(opponent)}</MenuItem>)}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>Nur deine Buchungsstunde wird gezählt. Beim ausgewählten Gegner wird keine Stunde abgezogen.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={() => setBookingDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" startIcon={<PersonAddAltRoundedIcon />} onClick={async () => {
            if (!pendingBooking) return; setSaving(true);
            try {
              const bookingGroupId = `${user.uid}-${Date.now()}`;
              for (const [index, hour] of pendingBooking.slotHours.entries()) await bookingsApi.create({
                date: format(pendingBooking.slotStart, "yyyy-MM-dd"), hour, platzNummer,
                bookingGroupId, resultPrimary: index === 0,
                ...(opponentId ? { opponentId } : {})
              });
              setBookingDialogOpen(false); setPendingBooking(null); setTrigger(x => x + 1);
            } finally { setSaving(false); }
          }} disabled={saving}>{saving ? "Speichert …" : "Verbindlich buchen"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Spielergebnis eintragen</DialogTitle>
        <DialogContent><Typography color="text.secondary" sx={{ mb: 2 }}>Das Ergebnis wird einmalig gespeichert und aktualisiert die Rangliste.</Typography>
          <Stack spacing={1.5}>{resultSets.map((set, index) => <Stack direction="row" spacing={1} alignItems="center" key={index}>
            <Typography sx={{ width: 58, fontWeight: 700 }}>{index + 1}. Satz</Typography>
            <TextField type="number" size="small" label="Ich" value={set.home} inputProps={{ min: 0 }} onChange={e => setResultSets(all => all.map((item, i) => i === index ? { ...item, home: e.target.value } : item))} />
            <TextField type="number" size="small" label="Gegner" value={set.away} inputProps={{ min: 0 }} onChange={e => setResultSets(all => all.map((item, i) => i === index ? { ...item, away: e.target.value } : item))} />
          </Stack>)}</Stack>{resultError && <Alert severity="error" sx={{ mt: 2 }}>{resultError}</Alert>}
        </DialogContent>
        <DialogActions><Button onClick={() => setResultDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" startIcon={<SportsScoreRoundedIcon />} disabled={saving} onClick={async () => { setResultError(""); setSaving(true); try { await matchesApi.saveBookingResult(selectedEvent.resource.id, resultSets); setResultDialogOpen(false); setModalOpen(false); setTrigger(x => x + 1); } catch (error) { setResultError(error.message); } finally { setSaving(false); } }}>Ergebnis speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Detailfenster für alle Buchungen und Serientermine */}
      <Modal open={modalOpen} onClose={() => { setModalOpen(false); setSelectedEvent(null); }}>
        <Box sx={{
          mt: 10,
          mx: "auto",
          p: 3,
          bgcolor: "white",
          maxWidth: 350,
          borderRadius: 2,
          boxShadow: 6,
          display: "flex",
          flexDirection: "column"
        }}>
          {selectedEvent && (
            <>
              <Typography variant="h6">{selectedEvent.isSeries ? "Serientermin" : "Buchungsdetails"}</Typography>
              <Typography color="text.secondary" sx={{ mt: .5 }}>
                {selectedEvent.start.toLocaleDateString("de-AT", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })}<br />
                {selectedEvent.start.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })} – {selectedEvent.end.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })} Uhr · Platz {selectedEvent.platzNummer || 1}
              </Typography>
              {selectedEvent.isSeries ? <>
                <Typography fontWeight={800} sx={{ mt: 2 }}>{selectedEvent.title}</Typography>
                <Typography sx={{ mt: 1 }}><b>Teilnehmer:</b> {selectedEvent.tooltipTeilnehmer || "Keine Teilnehmer hinterlegt"}</Typography>
              </> : <>
                <Typography sx={{ mt: 2 }}><b>Gebucht von:</b> {selectedEvent.bookerName}</Typography>
                <Typography sx={{ mt: .5 }}><b>Gegner/in:</b> {selectedEvent.opponentName || "Nicht angegeben"}</Typography>
                {selectedEvent.resource.result && <Box sx={{ mt: 2, p: 1.5, bgcolor: "rgba(57,134,91,.08)", borderRadius: 2 }}>
                  <Typography fontWeight={900} color="success.main">Ergebnis</Typography>
                  <Typography fontWeight={800}>{selectedEvent.resource.result.sets?.map(set => `${set.home}:${set.away}`).join(" · ") || "Erfasst"}</Typography>
                  <Typography fontSize={13} color="text.secondary">Sieger/in: {userMap[selectedEvent.resource.result.winnerId] || "Ergebnis eingetragen"}</Typography>
                </Box>}
                {selectedEvent.resource.opponentId && selectedEvent.end <= new Date() && selectedEvent.resource.resultPrimary !== false && selectedEvent.mayEditResult && !selectedEvent.resource.result && <Button sx={{ mt: 2 }} variant="contained" color="secondary" startIcon={<SportsScoreRoundedIcon />} onClick={() => { setResultSets([{ home: "", away: "" }, { home: "", away: "" }, { home: "", away: "" }]); setResultError(""); setResultDialogOpen(true); }}>Ergebnis eintragen</Button>}
                {selectedEvent.isOwn && !selectedEvent.resource.result && selectedEvent.start > new Date() && <Button sx={{ mt: 2 }} variant="outlined" color="error" onClick={async () => {
                  await Promise.all((selectedEvent.bookingIds || [selectedEvent.id]).map(bookingId => bookingsApi.remove(bookingId)));
                  setModalOpen(false); setTrigger(x => x + 1);
                }}>Buchung stornieren</Button>}
              </>}
              <Button sx={{ mt: 2 }} onClick={() => { setModalOpen(false); setSelectedEvent(null); }}>Schließen</Button>
            </>
          )}
        </Box>
      </Modal>
      <Typography sx={{ mt: 2, fontSize: 14, color: "#888" }}>
        Eigene Buchungen grün, „Rad“-Buchungen orange, andere blau. Ein ausgewählter Gegner wird direkt im Termin angezeigt.<br />
        Am Handy: Zum Scrollen normal wischen; zum Buchen einen freien Zeitpunkt kurz gedrückt halten und anschließend die gewünschten Stunden markieren.<br />
        Am Computer erfolgt die zeitliche Auswahl wie gewohnt mit der Maus.<br />
        Maximal zwei eigene Buchungen insgesamt (einschließlich Serie);
        während einer aktiven Serie (zwischen Start- und Enddatum) nur eine möglich, unabhängig vom Platz!<br />
        Platz ist oben umschaltbar.
      </Typography>
    </Box>
  );
}